import { API_CONFIG } from '../config/api';
import { randomUserService } from './randomUserService';

export interface HealthResponse {
  status: string;
  timestamp: string;
  version: string;
  services: {
    database: string;
    mcp: string;
    ai: string;
  };
}

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  toolCalls?: ToolCall[];
}

export interface ToolCall {
  id: string;
  name: string;
  arguments: any;
  result?: any;
  duration?: number;
}

export interface Conversation {
  sessionId: string;
  messages: ConversationMessage[];
  createdAt: string;
  updatedAt: string;
}

export interface MCPServerStatus {
  name: string;
  connected: boolean;
  tools: number;
  resources: number;
  prompts: number;
  lastSeen: string;
}

export interface AIAgentStatus {
  status: string;
  activeConversations: number;
  totalQueries: number;
  averageResponseTime: number;
  mcpConnections: number;
}

export interface SupportTicket {
  id: string;
  customerId: string;
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  createdAt: string;
  updatedAt: string;
}

export interface AnalyticsData {
  totalConversations: number;
  resolvedQueries: number;
  averageResponseTime: number;
  customerSatisfaction: number;
  topIssues: { issue: string; count: number }[];
  mcpToolUsage: { tool: string; usage: number }[];
  conversationTrends: { date: string; count: number }[];
}

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const config = { ...defaultOptions, ...options };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Generic GET method
  async get<T = any>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  // Health and status endpoints
  async getHealth(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health');
  }

  async getMCPStatus(): Promise<MCPServerStatus[]> {
    return this.request<MCPServerStatus[]>('/api/mcp/status');
  }

  async getAIAgentStatus(): Promise<AIAgentStatus> {
    return this.request<AIAgentStatus>('/api/agent/status');
  }

  // Conversation endpoints
  async sendMessage(message: string, sessionId?: string, userId?: string): Promise<{
    response: string;
    sessionId: string;
    toolCalls?: ToolCall[];
  }> {
    return this.request('/api/conversations', {
      method: 'POST',
      body: JSON.stringify({ message, sessionId, userId }),
    });
  }

  async getConversation(sessionId: string): Promise<Conversation> {
    return this.request<Conversation>(`/api/conversations/${sessionId}`);
  }

  async getConversations(limit: number = 10): Promise<Conversation[]> {
    return this.request<Conversation[]>(`/api/conversations?limit=${limit}`);
  }

  // Support ticket endpoints
  async createTicket(ticket: Omit<SupportTicket, 'id' | 'createdAt' | 'updatedAt'>): Promise<SupportTicket> {
    return this.request('/api/tickets', {
      method: 'POST',
      body: JSON.stringify(ticket),
    });
  }

  async getTickets(customerId?: string): Promise<SupportTicket[]> {
    const endpoint = customerId 
      ? `/api/tickets?customerId=${customerId}`
      : '/api/tickets';
    return this.request<SupportTicket[]>(endpoint);
  }

  async updateTicket(ticketId: string, updates: Partial<SupportTicket>): Promise<SupportTicket> {
    return this.request(`/api/tickets/${ticketId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // Analytics endpoints
  async getAnalytics(period: string = '7d'): Promise<AnalyticsData> {
    return this.request<AnalyticsData>(`/api/analytics?period=${period}`);
  }

  async getCustomerAnalytics(customerId: string): Promise<{
    totalConversations: number;
    lastActivity: string;
    commonIssues: string[];
    satisfaction: number;
  }> {
    return this.request(`/api/analytics/customer/${customerId}`);
  }

  // Authentication endpoints (mock for now)
  async login(email: string, password: string): Promise<{
    user: {
      id: string;
      name: string;
      email: string;
      role: 'customer' | 'agent' | 'admin';
      avatar?: string;
    };
    token: string;
  }> {
    // Mock login - in real implementation, this would validate credentials
    return new Promise(async (resolve) => {
      setTimeout(async () => {
        let user;
        let role: 'customer' | 'agent' | 'admin';
        
        if (email.includes('admin')) {
          role = 'admin';
        } else if (email.includes('agent')) {
          role = 'agent';
        } else {
          role = 'customer';
        }

        try {
          // Generate random user data for demo accounts
          const demoUser = await randomUserService.getDemoUser(role);
          
          user = {
            id: `${role}-${Date.now()}`,
            name: demoUser.name,
            email: email,
            role: role,
            avatar: demoUser.avatar,
          };
        } catch (error) {
          console.warn('Failed to generate random user, using fallback');
          // Fallback to static names if random user service fails
          const fallbackNames = {
            admin: 'Admin User',
            agent: 'Support Agent',
            customer: 'Customer User',
          };
          
          user = {
            id: `${role}-${Date.now()}`,
            name: fallbackNames[role],
            email: email,
            role: role,
            avatar: `https://randomuser.me/api/portraits/men/65.jpg`, // Default fallback avatar
          };
        }
        
        resolve({
          user,
          token: 'mock-jwt-token',
        });
      }, 1000);
    });
  }

  // System management endpoints (admin only)
  async restartMCPServer(serverName: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/admin/mcp/restart/${serverName}`, {
      method: 'POST',
    });
  }

  async updateMCPServerConfig(serverName: string, config: any): Promise<{ success: boolean }> {
    return this.request(`/api/admin/mcp/config/${serverName}`, {
      method: 'PUT',
      body: JSON.stringify({ config }),
    });
  }

  async getSystemLogs(level: string = 'info', limit: number = 100): Promise<{
    logs: Array<{
      timestamp: string;
      level: string;
      message: string;
      meta?: any;
    }>;
  }> {
    return this.request(`/api/admin/logs?level=${level}&limit=${limit}`);
  }
}

export const apiService = new ApiService(); 